import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, CheckCircle2, Calendar, Clock } from 'lucide-react';
import { submitContactForm } from '../data/mock';
import { useSearchParams } from 'react-router-dom';
import './ContactPage.css';
 
const CALENDLY_URL = 'https://calendly.com/contact-synaprocess/30min';
 
const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const subjectFromUrl = searchParams.get('subject');
  
  const [activeTab, setActiveTab] = useState(subjectFromUrl ? 'appointment' : 'contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
 
  const observerRef = useRef(null);
 
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
 
    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observerRef.current?.observe(el));
 
    return () => observerRef.current?.disconnect();
  }, []);
 
  // Charge le script Calendly quand l'onglet rendez-vous est actif
  useEffect(() => {
    if (activeTab === 'appointment') {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [activeTab]);
 
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await submitContactForm(formData);
      if (response.success) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
 
  const handleContactChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
 
  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="section-container">
          <div className="contact-hero-content animate-on-scroll">
            <h1 className="contact-title">
              Structurons ensemble <span className="text-gradient">votre croissance</span>
            </h1>
            <p className="contact-subtitle">
              Prenez rendez-vous pour un diagnostic gratuit ou contactez-nous directement
            </p>
          </div>
        </div>
      </section>
 
      <section className="section-padding">
        <div className="section-container">
          <div className="contact-layout">
            <div className="contact-info animate-on-scroll">
              <div className="info-card">
                <h2>Parlons de vos enjeux</h2>
                <p>
                  Notre équipe est à votre écoute pour comprendre vos défis opérationnels 
                  et vous proposer un plan d'action sur-mesure.
                </p>
              </div>
 
              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">
                    <Mail size={24} />
                  </div>
                  <div className="method-content">
                    <h3>Email</h3>
                    <a href="mailto:contact@synaprocess.fr">contact@synaprocess.fr</a>
                    <p className="method-note">Réponse sous 24h</p>
                  </div>
                </div>
 
                <div className="contact-method">
                  <div className="method-icon">
                    <Phone size={24} />
                  </div>
                  <div className="method-content">
                    <h3>Téléphone</h3>
                    <a href="tel:+33695341006">06 95 34 10 06</a>
                    <p className="method-note">Lun-Ven, 9h-18h</p>
                  </div>
                </div>
 
                <div className="contact-method">
                  <div className="method-icon">
                    <MapPin size={24} />
                  </div>
                  <div className="method-content">
                    <h3>Adresse</h3>
                    <p>Paris, France</p>
                    <p className="method-note">Sur rendez-vous uniquement</p>
                  </div>
                </div>
              </div>
 
              <div className="info-badge">
                <CheckCircle2 size={20} />
                <span>Diagnostic gratuit • Sans engagement • Réponse rapide</span>
              </div>
            </div>
 
            <div className="contact-forms animate-on-scroll">
              <div className="form-tabs">
                <button
                  className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
                  onClick={() => setActiveTab('contact')}
                >
                  <Mail size={18} />
                  Contact
                </button>
                <button
                  className={`tab-btn ${activeTab === 'appointment' ? 'active' : ''}`}
                  onClick={() => setActiveTab('appointment')}
                >
                  <Calendar size={18} />
                  Rendez-vous
                </button>
              </div>
 
              {submitSuccess && (
                <div className="success-message">
                  <CheckCircle2 size={20} />
                  <span>Votre message a été envoyé avec succès !</span>
                </div>
              )}
 
              {activeTab === 'contact' && (
                <form className="contact-form" onSubmit={handleContactSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Nom complet *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleContactChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="company">Entreprise</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleContactChange}
                      />
                    </div>
                  </div>
 
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleContactChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Téléphone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleContactChange}
                      />
                    </div>
                  </div>
 
                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleContactChange}
                      placeholder="Décrivez votre situation et vos enjeux..."
                      required
                    ></textarea>
                  </div>
 
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-large btn-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </button>
                </form>
              )}
 
              {activeTab === 'appointment' && (
                <div className="calendly-wrapper">
                  <div className="appointment-note" style={{ marginBottom: '1.5rem' }}>
                    <Clock size={16} />
                    <span>Durée : 30 minutes • Visioconférence ou téléphone</span>
                  </div>
                  <div
                    className="calendly-inline-widget"
                    data-url={CALENDLY_URL}
                    style={{ minWidth: '320px', height: '700px' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
 
      <section className="contact-faq">
        <div className="section-container">
          <div className="faq-content animate-on-scroll">
            <h2>Questions avant de nous contacter ?</h2>
            <div className="faq-items">
              <div className="faq-item-simple">
                <h3>Le diagnostic est-il vraiment gratuit ?</h3>
                <p>
                  Oui, absolument. C'est un échange de 30 minutes pour comprendre vos enjeux 
                  et voir si SYNA peut vous aider. Sans engagement.
                </p>
              </div>
              <div className="faq-item-simple">
                <h3>Sous quel délai aurez-vous une réponse ?</h3>
                <p>
                  Nous répondons sous 48h maximum à tous les messages et demandes de rendez-vous.
                </p>
              </div>
              <div className="faq-item-simple">
                <h3>Travaillez-vous partout en France ?</h3>
                <p>
                  Oui, nous accompagnons des entreprises dans toute la France.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
 
export default ContactPage;